// SettingsUtils.js - Utility functions for safe settings access

/**
 * Safely checks if a payment method is disabled
 * Returns false (enabled) if the setting doesn't exist
 * @param {Object} settings - Settings object
 * @param {string} paymentMethod - Payment method to check ('cash', 'online', 'yape')
 * @returns {boolean} - true if disabled, false if enabled or undefined
 */
export const isPaymentMethodDisabled = (settings, paymentMethod) => {
  if (!settings) return false;

  switch (paymentMethod) {
    case 'cash':
      return settings.disable_cash || false;
    case 'online':
      return settings.disable_online || false;
    case 'yape':
      return settings.disable_yape || false;
    default:
      return false;
  }
};

/**
 * Safely gets a boolean setting with default fallback
 * @param {Object} settings - Settings object
 * @param {string} settingKey - Setting key to check
 * @param {boolean} defaultValue - Default value if setting doesn't exist
 * @returns {boolean} - Setting value or default
 */
export const getSafeBooleanSetting = (settings, settingKey, defaultValue = false) => {
  if (!settings || settings[settingKey] === undefined || settings[settingKey] === null) {
    return defaultValue;
  }
  return Boolean(settings[settingKey]);
};

/**
 * Safely checks if negative balance is allowed
 * @param {Object} settings - Settings object
 * @returns {boolean} - true if negative balance is allowed
 */
export const isNegativeBalanceAllowed = (settings) => {
  return getSafeBooleanSetting(settings, 'negativeBalance', false);
};

/**
 * Get available payment methods based on settings
 * @param {Object} settings - Settings object
 * @param {Array} providers - Payment providers array
 * @param {Function} t - Translation function
 * @returns {Array} - Array of available payment options
 */
export const getAvailablePaymentMethods = (settings, providers, t) => {
  let arr = [{ label: t("wallet"), value: 0, cat: "wallet" }];
  let val = 0;

  if (!isPaymentMethodDisabled(settings, 'online') && providers && providers.length > 0) {
    val++;
    arr.push({ label: t("card"), value: val, cat: "card" });
  }

  if (!isPaymentMethodDisabled(settings, 'cash')) {
    val++;
    arr.push({ label: t("cash"), value: val, cat: "cash" });
  }

  if (!isPaymentMethodDisabled(settings, 'yape')) {
    val++;
    arr.push({ label: t("yape"), value: val, cat: "yape" });
  }

  return arr;
}; 